import React from 'react';
import { Box, Card, CardActionArea, Typography, LinearProgress, Chip, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getEqpStateStyle, getEqpStateLabel, formatSince } from './eqpState';
import EqpToolpathThumbnail from './EqpToolpathThumbnail';

// 모니터링 보드 카드 한 장. 왼쪽 굵은 테두리 + 옅은 배경으로 상태를 한눈에 구분한다(2026-10-10).
const EqpMonitorCard = ({ row, onClick }) => {
  const theme = useTheme();
  const stale = Boolean(row.stale);
  const style = getEqpStateStyle(theme, row.runSt, stale);
  const since = formatSince(row.stateSinceDt);
  // 데이터 수신 지연: 에이전트 자체가 오프라인이거나, 마지막 수신이 오래돼 stale 로 내려온 경우
  const agentWarn = row.agentOnline === false || stale;
  // 2026-10-23 "기준시간 가동률" 계획 - 기준시간(todayStdHr) 기반 값이 주 지표, 기존 수집-구간
  // 기준 값(todayUtilPct)은 보조 지표로 이름을 바꿔("가동 비율(수집 기준)") 같이 보여준다.
  const hasPlannedUtil = row.todayPlannedUtilPct != null;
  const plannedPctDisplay = hasPlannedUtil ? Math.max(0, Number(row.todayPlannedUtilPct)) : 0;
  const plannedBarPct = Math.min(100, plannedPctDisplay); // 100% 넘어도 막대는 꽉 채움
  const collectedPct = Math.min(100, Math.max(0, Number(row.todayUtilPct) || 0));

  return (
    <Card
      variant="outlined"
      sx={{
        borderLeft: `6px solid ${style.main}`,
        borderStyle: style.dashed ? 'dashed' : 'solid',
        ...(style.dashed ? { borderColor: style.main } : {}),
        bgcolor: style.bg,
        height: '100%',
      }}
    >
      <CardActionArea onClick={onClick} sx={{ height: '100%', p: 1.5, alignItems: 'stretch', display: 'block' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>{row.eqpNm}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {row.eqpCd}{row.location ? ` · ${row.location}` : ''}
            </Typography>
          </Box>
          {agentWarn && (
            <Tooltip title="데이터 수신 지연">
              <WarningAmberIcon color="warning" fontSize="small" />
            </Tooltip>
          )}
        </Box>

        <Box sx={{ mt: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: style.main }}>
            {getEqpStateLabel(row.runSt, stale)}
          </Typography>
          {since && <Typography variant="caption" color="text.secondary">{since}</Typography>}
        </Box>

        {row.runSt === 'RUNNING' && !stale && (
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
            <EqpToolpathThumbnail eqpUuid={row.eqpUuid} />
          </Box>
        )}

        {row.alarmYn === 'Y' && row.alarmTxt && (
          <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }} noWrap>
            ⚠ {row.alarmTxt}
          </Typography>
        )}

        <Box sx={{ mt: 1.25, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', columnGap: 1, rowGap: 0.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">프로그램</Typography>
            <Typography variant="body2" noWrap>
              {row.prgNo || '-'}{row.mainPrgNo ? ` / ${row.mainPrgNo}` : ''}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">금일 가공수</Typography>
            <Typography variant="body2">{row.todayPartCnt ?? 0}개</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">주축 회전수</Typography>
            <Typography variant="body2">{row.spindleRpm != null ? `${row.spindleRpm} rpm` : '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">이송속도</Typography>
            <Typography variant="body2">{row.feed != null ? row.feed : '-'}</Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 1.25 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">금일 가동률(기준시간)</Typography>
            <Typography variant="caption" fontWeight={600}>
              {hasPlannedUtil ? `${row.todayPlannedUtilPct}%` : '해당없음'}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate" value={plannedBarPct}
            color={plannedPctDisplay > 100 ? 'warning' : 'primary'}
            sx={{ height: 5, borderRadius: 1, mt: 0.25 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">가동 비율(수집 기준)</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.todayUtilPct != null ? `${row.todayUtilPct}%` : '-'}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate" value={collectedPct}
            color="inherit"
            sx={{ height: 3, borderRadius: 1, mt: 0.25, opacity: 0.5 }}
          />
        </Box>

        <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.agentNm || '담당 에이전트 없음'}
          </Typography>
          {Number(row.todayAlarmCnt) > 0 && (
            <Chip size="small" color="error" variant="outlined" label={`알람 ${row.todayAlarmCnt}`} />
          )}
        </Box>
      </CardActionArea>
    </Card>
  );
};

export default EqpMonitorCard;
